import React, { useState, useEffect } from "react";
import MUIDataTable from "mui-datatables";
import { useSelector, useDispatch } from "react-redux";
import LoadingOverlay from "react-loading-overlay-ts";

// MUI imports
import {
  Box,
  CircularProgress,
  ThemeProvider,
  Grid,
  Card,
  Typography,
  CardContent,
  Paper,
  Container,
  Tooltip,
  IconButton,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";

// Project imports
import {
  exportPcaReport,
  resetPcaFindCm,
  getAllRegisteredCmPca,
  getCmPcaById,
  pcaFetchAllActiveSkills,
} from "../../../../../Actions";
import Page from "../../../../../Components/Dashboard/Page";
import {
  EditIcon,
  MuiDataTableTheme,
} from "../../../../../Components/MuiDataTableTheme/MuiDataTableTheme";
import { ElevatedGreenButton } from "../../../../../Components/Button/CustomButton";
import { poppinsCustomTheme } from "../../../../../Styles/GlobalStyles/PoppinsTheme";
import DashboardCard from "../../../../../Components/Global/DashboardCard";
import CreatePCADialog from "../../../../../Components/CreatePCADialog";
import PCAEditDialog from "../../../../../Components/PCAEditDialog";

const ColumnTextStyle = {
  textTransform: "capitalize",
  textAlign: "left",
  color: "#212b36",
  fontSize: "0.875rem",
  fontWeight: 600,
};

const DownloadIcon = () => {
  const pcaState = (state) => state.postCampActivityReducer;

  const { fetchingReport } = useSelector(pcaState);

  if (fetchingReport) {
    return <CircularProgress size={20} />;
  } else {
    return <CloudDownloadOutlinedIcon />;
  }
};

const ManagePostCampActivities = () => {
  const globalDispatch = useDispatch();
  const pcaState = (state) => state.postCampActivityReducer;
  const getUserDetails = (state) => state.usersReducer;
  const { authUser } = useSelector(getUserDetails);
  const {
    fetchingReport,
    listOfRegisteredCmPca,
    fetchingRegisteredCm,
    registeredCmMetaData,
    // fetchRegisteredCmSuccess,
    // registeredCmMetaData,
  } = useSelector(pcaState);

  const [addPCADialogOpen, setAddPCADialogOpen] = useState(false);
  const [editPCADialogOpen, setEditPCADialogOpen] = useState(false);

  function handleOpenAddPCADialog() {
    setAddPCADialogOpen(true);
  }

  function handleCloseAddPCADialog() {
    setAddPCADialogOpen(false);
    globalDispatch(resetPcaFindCm());
  }

  function handleOpenEditPCADialog(id) {
    globalDispatch(getCmPcaById(id));
    globalDispatch(pcaFetchAllActiveSkills());
    setEditPCADialogOpen(true);
  }

  function handleCloseEditPCADialog() {
    setEditPCADialogOpen(false);
    // globalDispatch(resetPcaFindCm());
  }

  function handleTableChangePage(currentPage) {
    document
      .getElementById("dashboard-zone")
      .scrollIntoView({ behavior: "smooth" });

    globalDispatch(
      getAllRegisteredCmPca({
        PageNumber: currentPage + 1,
      })
    );
  }

  useEffect(() => {
    globalDispatch(getAllRegisteredCmPca());
  }, []);

  const cards = [
    {
      title: "Total Number of Registered Corp Members",
      value: registeredCmMetaData?.totalCount,
      icon: "null",
      bgcolor: "#C8E7FD",
      iconColor: "#0095FF",
      theIcon: "fluent:people-team-16-filled",
      id: "total-partners",
    },
  ];

  const datatableColumns = [
    {
      name: "cmName",
      label: "Name",
      options: {
        setCellProps: () => ({
          style: { paddingLeft: "24px" },
        }),
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography
              style={{ paddingLeft: "24px" }}
              sx={ColumnTextStyle}
              key={index}
            >
              {column.label}
            </Typography>
          );
        },
      },
    },
    {
      name: "cmStateCode",
      label: "State Code",
      options: {
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
      },
    },
    {
      name: "skill",
      label: "Skill Set",
      options: {
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value) => {
          return <>{value.name}</>;
        },
      },
    },
    {
      name: "trainingCenterName",
      label: "Name of Training Center",
      options: {
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value) => {
          if (
            authUser.role !== "SAED_NDHQ" &&
            authUser.role !== "SAED_State_Coordinator" &&
            authUser.role !== "SAED_State"
          ) {
            return (
              <Tooltip title={"Edit Details"}>
                <IconButton
                  size="small"
                  onClick={() => handleOpenEditPCADialog(value)}
                >
                  <EditIcon icon="icon-park-outline:edit" />
                </IconButton>
              </Tooltip>
            );
          }
        },
      },
    },
  ];

  const options = {
    filter: false,
    expandableRows: false,
    disableRipple: true,
    elevation: 0,
    fixedHeader: true,
    print: false,
    responsive: "standard",
    search: false,
    selectableRows: "none",
    onDownload: () => {
      if (!fetchingReport) {
        globalDispatch(exportPcaReport());
      }
      return false;
    },
    customToolbar: () => {
      if (
        authUser.role !== "SAED_NDHQ" &&
        authUser.role !== "SAED_State_Coordinator" &&
        authUser.role !== "SAED_State"
      ) {
        return (
          <ElevatedGreenButton
            startIcon={<AddOutlinedIcon />}
            onClick={handleOpenAddPCADialog}
          >
            Add Post Camp Activity
          </ElevatedGreenButton>
        );
      }
    },
    textLabels: {
      toolbar: {
        downloadCsv: "Download Report",
      },
    },

    // pagination features
    count: registeredCmMetaData?.totalCount,
    jumpToPage: true,
    page: registeredCmMetaData?.pageNumber - 1,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: [10],
    serverSide: true,
    onChangePage: (curPage) => handleTableChangePage(curPage),
  };

  const components = {
    icons: {
      DownloadIcon,
    },
  };

  return (
    <ThemeProvider theme={poppinsCustomTheme}>
      <Page title="SAED | Manage Post Camp Activities">
        <Container maxWidth="xl">
          <Box sx={{ pb: 2 }}>
            <Typography
              variant="h5"
              style={{ fontWeight: 600, color: "#212B36" }}
            >
              Manage Post Camp Activities
            </Typography>
          </Box>
          <Box sx={{ pb: 3 }}>
            <Grid id={"dashboard-zone"} container spacing={3}>
              {cards.map((card) => {
                return (
                  card?.value !== null && (
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      md={4}
                      key={`${card.title}-${card.value}`}
                    >
                      <DashboardCard
                        value={card.value}
                        title={card.title}
                        bgcolor={card.bgcolor}
                        iconColor={card.iconColor}
                        theIcon={card.theIcon}
                        id={card.id}
                        valueType={card.valueType}
                      />
                    </Grid>
                  )
                );
              })}
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box>
                <Paper
                  sx={{
                    boxShadow: "0 1px 2px 0 rgb(145 158 171 / 24%)",
                    borderRadius: "20px",
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{ padding: "0px !important", borderRadius: "20px" }}
                  >
                    <CardContent sx={{ padding: "0px !important" }}>
                      <ThemeProvider theme={MuiDataTableTheme}>
                        <LoadingOverlay
                          active={fetchingRegisteredCm}
                          styles={{
                            overlay: (base) => ({
                              ...base,
                              background: "rgba(0, 0, 0, 0.15)",
                            }),
                          }}
                          spinner
                          text="Fetching Data for You..."
                        >
                          <div id="partners-datatable">
                            <MUIDataTable
                              title={
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={10}
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    sx={{ color: "#05004E", fontWeight: 600 }}
                                  >
                                    Registered Corp Members
                                  </Typography>
                                </Box>
                              }
                              columns={datatableColumns}
                              options={options}
                              style={{ transition: "0.7s" }}
                              data={listOfRegisteredCmPca}
                              components={components}
                            />
                          </div>
                        </LoadingOverlay>
                      </ThemeProvider>
                    </CardContent>
                  </Card>
                </Paper>
              </Box>
            </Grid>
          </Grid>
          <CreatePCADialog
            isOpen={addPCADialogOpen}
            handleClose={handleCloseAddPCADialog}
          />
          <PCAEditDialog
            isOpen={editPCADialogOpen}
            handleClose={handleCloseEditPCADialog}
          />
        </Container>
      </Page>
    </ThemeProvider>
  );
};

export default ManagePostCampActivities;
